import { ArrowForwardIcon } from "@chakra-ui/icons";
import { Container, Card, CardHeader, Heading, Button, CardBody, Text, Divider, Box, Flex, Spacer, Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, ListItem, Badge, OrderedList, FormControl, FormLabel, HStack, NumberInputField, NumberInput, NumberDecrementStepper, NumberIncrementStepper, NumberInputStepper } from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { useEffect, useState } from "react";
import SubTargetForm from "./subTargetForm/SubTargetForm";
import { SubTarget } from "types/SubTarget";
import MyBreadcrumb from "components/breadcrumb/Breadcrumb";
import { Breadcrumb } from "types/Breadcrumb";

type TargetProps = {
  isReadOnly?: boolean;
};


/**
 * Component representing a target view with subtargets and a form for editing or viewing details.
 *
 * @param {TargetProps} props - The properties for the Target component.
 * @param {boolean} [props.isReadOnly=true] - Flag indicating if the view is read-only or editable.
 *
 * @returns {JSX.Element} The rendered Target component.
 *
 * @component
 *
 * @example
 * // Example usage of Target component
 * <Target isReadOnly={false} />
 *
 * @remarks
 * This component uses various hooks and components from React and Chakra UI to display and manage
 * the state of a target and its subtargets. It includes breadcrumb navigation, an accordion for
 * displaying subtargets and a form for selecting a time period.
 *
 * @hook
 * - useParams: To get the URL parameters.
 * - useLocation: To get the current location object.
 * - useSelector: To access the Redux store state.
 * - useState: To manage local state for selected subtarget and year.
 * - useEffect: To handle side effects, such as setting initial state based on location.
 *
 * @param {Object} params - The URL parameters.
 * @param {string} params.targetIndex - The index of the target.
 * @param {Object} location - The current location object.
 * @param {Object} location.state - The state object passed via navigation.
 * @param {string} location.state.subTargetId - The ID of the selected subtarget.
 * @param {string} location.state.timePeriod - The selected time period.
 * @param {Object} state - The Redux store state.
 * @param {Object} state.documents - The documents state in the Redux store.
 * @param {string} state.documents.selectedDocumentId - The ID of the selected document.
 * @param {Array} state.documents.documents - The list of documents.
 * @param {Object} state.documents.documents[].targets - The list of targets in a document.
 * @param {string} state.documents.documents[].targets[].index - The index of a target.
 * @param {string} state.documents.documents[].targets[].title - The title of a target.
 * @param {string} state.documents.documents[].targets[].description - The description of a target.
 * @param {Array} state.documents.documents[].targets[].subtargets - The list of subtargets in a target.
 * @param {string} state.documents.documents[].targets[].subtargets[].title - The title of a subtarget.
 * @param {string} state.documents.documents[].targets[].subtargets[].index - The index of a subtarget.
 * @param {string} state.documents.documents[].targets[].subtargets[].description - The description of a subtarget.
 * @param {string} state.documents.documents[].targets[].subtargets[].id - The ID of a subtarget.
 * @param {string} state.documents.documents[].targets[].subtargets[].timePeriod - The time period of a subtarget.
 */
export default function Target({ isReadOnly = true }: TargetProps) {

  const params = useParams();
  const location = useLocation();
  const { targetIndex } = params;
  const selectedDocumentId = useSelector((state: any) => state.documents.selectedDocumentId);
  const selectedDocument = (useSelector((state: any) => state.documents.documents.find((doc: any) => doc._id === selectedDocumentId)));
  const target = selectedDocument.targets.find((target: any) => target.index === targetIndex);

  const [selectedSubtarget, setSelectedSubtarget] = useState<SubTarget | null>(null);
  const [selectedYear, setSelectedYear] = useState<string | null>("");

  useEffect(() => {
    if (location && location.state) {
      setSelectedSubtarget(
        target?.subtargets.find(
          (subTarget: { _id: any }) => subTarget._id == location.state.subTargetId
        )
      )
      setSelectedYear(location.state.timePeriod);
    }
  })

  const handleOnChangeYear = (value: any) => {
    setSelectedYear(value);
    setSelectedSubtarget(
      target?.subtargets.find(
        (subTarget: { _id: any }) => subTarget._id == selectedSubtarget?._id
      )
    )
  };

  const breadcrumb: Breadcrumb = new Breadcrumb(
    [
      { label: "Obiettivi", link: "/targets", value: "", isActive: true },
      { label: `Obiettivo ${targetIndex}`, link: `/targets/${targetIndex}`, value: "", isActive: true },
      { label: `${isReadOnly ? 'Visualizza dati' : 'Modifica dati'}`, link: `/targets/${targetIndex}/${isReadOnly ? 'view' : 'edit'}`, value: "", isActive: true },
    ]
  );

  return (

    <section className="dashboard-first-section">
      <Container maxW="container.lg" maxH={"container.xl"} textAlign={"left"}>
        <MyBreadcrumb breadcrumb={breadcrumb} />

        <Card minH={"60vh"}>
          <CardHeader pb={0}>
            <HStack>
              <Heading as="h1" size="xl" >
                {
                  isReadOnly ? "Visualizzazione Dati: " : "Modifica Dati :"
                } Obiettivo {targetIndex} - {target?.title}
              </Heading>
            </HStack>
            <Text >
              {target?.description}
            </Text>
          </CardHeader>
          <CardBody>
            <Accordion defaultIndex={[0, 1]} allowMultiple mt={0}>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex='1' textAlign='left'>
                      <Heading as="h2" size={"sm"}>
                        Sotto obiettivi
                      </Heading>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>

                <AccordionPanel pb={4}>

                  <OrderedList
                    styleType={"none"}
                    margin={0}
                  >
                    {target?.subtargets.map((subtarget: SubTarget, index: any) => (
                      <ListItem key={subtarget._id}
                        mb={2}
                      >
                        <Flex alignItems={"center"} p={0}>
                          <Badge mr="2" width={"5%"} textAlign={"center"}>
                            {`${targetIndex}.${index + 1}`}
                          </Badge>
                          <Text width={"90%"} p={0}>{subtarget.title}</Text>
                          <Spacer />
                          <Button
                            width={"10%"}
                            size={"xs"}
                            colorScheme={isReadOnly ? "blue" : "yellow"}
                            rightIcon={isReadOnly ? <ArrowForwardIcon /> : <FaEdit />}
                            onClick={() => {
                              setSelectedSubtarget(subtarget);
                            }}
                            isDisabled={selectedSubtarget?._id === subtarget._id}
                          >
                            {
                              isReadOnly ? "Visualizza" : "Modifica"
                            }
                          </Button>
                        </Flex>
                        <Divider my={1} />
                      </ListItem>
                    ))}
                  </OrderedList>


                </AccordionPanel>
              </AccordionItem>

              <AccordionItem>


                <AccordionButton>
                  <Box flex='1' textAlign='left'>
                    <Heading as="h2" size={"sm"}>
                      Periodo d'inserimento
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>

                <AccordionPanel pb={4}>
                  <form>
                    <FormControl>
                      <FormLabel>Seleziona un periodo d'inserimento</FormLabel>

                      <NumberInput
                        step={1}
                        min={2023}
                        onChange={val => handleOnChangeYear(val)}
                      >
                        <NumberInputField
                          placeholder={`Inserisci un anno (e.g. ${new Date().getFullYear().toString()})`}
                        /><NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>

                  </form>
                </AccordionPanel>

              </AccordionItem>

            </Accordion>

            <SubTargetForm
              documentId={selectedDocumentId}
              targetId={target?._id}
              subTarget={selectedSubtarget}
              isReadOnly={isReadOnly}
              timePeriod={selectedYear}
            />
          </CardBody>
        </Card>
      </Container>
    </section>

  )

}
